import { Fragment, useState, useCallback, useId, useSyncExternalStore, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Inbox, Plus, Search, ArrowRightCircle, Loader2, Download, RotateCcw, Users } from 'lucide-react'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@quebon/ui/components/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Field, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Textarea } from '@quebon/ui/components/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@quebon/ui/components/table'
import { handleApiError } from '@/api/client'
import {
  fetchIntakeCustomers,
  fetchIntakeCustomerBreakdown,
  fetchIntakeCustomerDetail,
  fetchIntakeDetail,
  exportIntakeCustomersCsv,
  exportInboxCsv,
  createManualIntake,
  updateManualIntake,
  previewConversion,
  applyConversion,
  updateIntakeAttendance,
  updateIntakeSession,
  type AcquisitionChannel,
  type IntakeCustomerFilter,
  type IntakeCustomerItem,
  type InboxListItem,
  type InboxDetail,
  type ConversionPreview,
} from '@/api/lcCrm'
import {
  APPLICATION_STATUS_LABEL,
  ATTENDANCE_SUMMARY_LABEL,
  INBOX_STATUS_LABEL,
  INTAKE_ATTENDANCE_LABEL,
  INTAKE_CUSTOMER_KIND_LABEL,
  LEAD_STAGE_LABEL,
  SESSION_MODE_LABEL,
  SOURCE_TYPE_LABEL,
  formatKstDate,
  labelOf,
  formatPhone,
} from './crmLabels'
import { EmptyRow, ErrorBlock, FilterSelect, LabeledSelect, LoadingBlock, Pagination } from './crmComponents'
import { ContactNotesPanel } from './ContactNotesPanel'
import { AcquisitionChannelField } from './AcquisitionChannelField'
import { acquisitionChannelLabel, acquisitionChannelPatch } from './acquisitionChannel'
import { IntakeApplicationDetails, applicationLabels } from './IntakeApplicationDetails'
import { FRANCHISE_TYPE_LABELS } from '../support/tabs/inquiryLabels'

const PAGE_SIZE = 20

function statusBadgeVariant(status: string): 'default' | 'secondary' | 'outline' {
  switch (status) {
    case 'PENDING': return 'default'
    case 'CONVERTED': return 'secondary'
    default: return 'outline'
  }
}

function attendanceSummaryVariant(summary: string): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (summary) {
    case 'ATTENDED': return 'default'
    case 'SCHEDULED': return 'secondary'
    case 'NO_SHOW': return 'destructive'
    default: return 'outline'
  }
}

const HAS_LEAD_ITEMS = [
  { value: 'true', label: '리드 있음' },
  { value: 'false', label: '리드 없음' },
]

/**
 * 접수함 — 접수 고객(사람) 단위 목록 (ADR-0008, 2026-09-15).
 *
 * 한 사람이 회차를 바꿔 다시 신청하거나 설명회 신청과 가맹 문의를 둘 다 내도 한 행이다. 접수(건)는
 * 상세의 이력으로 보이고, 참석 기록·회차 수정·리드 전환은 그 이력 행에서 실행한다. 예전 건 단위
 * 목록은 같은 사람이 세 줄로 보여 "몇 명이 신청했는지"를 화면에서 읽을 수 없었다.
 */
export function CrmInboxPage() {
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState<IntakeCustomerFilter>({ page: 0, size: PAGE_SIZE })
  const [searchInput, setSearchInput] = useState('')
  const [manualOpen, setManualOpen] = useState(false)
  const [exportOriginalOpen, setExportOriginalOpen] = useState(false)
  const [detailContactId, setDetailContactId] = useState<number | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['lc-crm-inbox-customers', filter],
    queryFn: () => fetchIntakeCustomers(filter),
  })

  const breakdown = useQuery({
    queryKey: ['lc-crm-inbox-customers-breakdown'],
    queryFn: fetchIntakeCustomerBreakdown,
  })

  const handleSearch = useCallback(() => {
    setFilter((prev) => ({ ...prev, search: searchInput || undefined, page: 0 }))
  }, [searchInput])

  const patchFilter = useCallback((patch: Partial<IntakeCustomerFilter>) => {
    setFilter((prev) => ({ ...prev, ...patch, page: 0 }))
  }, [])

  const resetFilter = useCallback(() => {
    setFilter({ page: 0, size: PAGE_SIZE })
    setSearchInput('')
  }, [])

  const invalidateInbox = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['lc-crm-inbox-customers'] })
    queryClient.invalidateQueries({ queryKey: ['lc-crm-inbox-customers-breakdown'] })
    queryClient.invalidateQueries({ queryKey: ['lc-crm-inbox-customer-detail'] })
    queryClient.invalidateQueries({ queryKey: ['lc-crm-dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['lc-crm-funnel'] })
  }, [queryClient])

  const downloadBlob = ({ blob, filename }: { blob: Blob; filename: string }) => {
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  }

  /** 접수 고객 CSV. 지금 화면의 filter가 그대로 내려간다 — 보이는 것과 받는 것이 같아야 한다. */
  const exportMutation = useMutation({
    mutationFn: () => exportIntakeCustomersCsv(filter),
    onSuccess: downloadBlob,
    onError: (err) => toast.error(handleApiError(err) || 'CSV 내려받기 실패'),
  })

  /**
   * 건 단위 원본 포함 CSV. 원본 PII를 포함하고 서버가 EXPORT_WITH_ORIGINAL audit을 남긴다
   * (설계 §19.5). 검색어만 같이 내려간다 — 사람 단위 필터는 건 단위 내보내기에 대응하는 값이 없다.
   */
  const exportOriginalMutation = useMutation({
    mutationFn: () => exportInboxCsv({ search: filter.search }, true),
    onSuccess: downloadBlob,
    onError: (err) => toast.error(handleApiError(err) || 'CSV 내려받기 실패'),
  })

  const hasFilter = Boolean(
    filter.kind || filter.attendanceSummary || filter.hasLead != null || filter.search,
  )

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">접수함</h2>
          <p className="text-sm text-muted-foreground">
            설명회 신청과 가맹 문의를 연락처 기준 한 사람으로 묶어 관리합니다
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={exportMutation.isPending}
            onClick={() => exportMutation.mutate()}
          >
            {exportMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
            CSV 내려받기
          </Button>
          <Button
            variant="outline"
            disabled={exportOriginalMutation.isPending}
            onClick={() => setExportOriginalOpen(true)}
          >
            원본 포함 CSV
          </Button>
          <Button onClick={() => setManualOpen(true)}>
            <Plus className="size-4" />
            수동 등록
          </Button>
        </div>
      </div>

      {breakdown.data && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryTile label="접수 고객" value={breakdown.data.total} emphasis />
          <SummaryTile label="설명회만" value={breakdown.data.briefingOnly} />
          <SummaryTile label="문의만" value={breakdown.data.franchiseOnly} />
          <SummaryTile label="둘 다" value={breakdown.data.both} />
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-muted/40 p-3">
        <Field className="min-w-56 flex-1">
          <FieldLabel htmlFor="inbox-search">검색</FieldLabel>
          <div className="relative">
            <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="inbox-search"
              className="pl-8"
              placeholder="이름, 전화번호로 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </Field>
        <FilterSelect
          className="w-40"
          label="구분"
          value={filter.kind}
          onChange={(value) => patchFilter({ kind: value })}
          items={Object.entries(INTAKE_CUSTOMER_KIND_LABEL).map(([value, label]) => ({ value, label }))}
        />
        <FilterSelect
          className="w-36"
          label="참석 요약"
          value={filter.attendanceSummary}
          onChange={(value) => patchFilter({ attendanceSummary: value })}
          items={Object.entries(ATTENDANCE_SUMMARY_LABEL).map(([value, label]) => ({ value, label }))}
        />
        <FilterSelect
          className="w-36"
          label="리드"
          value={filter.hasLead == null ? undefined : String(filter.hasLead)}
          onChange={(value) => patchFilter({ hasLead: value == null ? undefined : value === 'true' })}
          items={HAS_LEAD_ITEMS}
        />
        <div className="flex items-center gap-2 pb-0.5">
          <Button variant="outline" onClick={handleSearch}>검색</Button>
          <Button variant="ghost" disabled={!hasFilter} onClick={resetFilter}>
            <RotateCcw className="size-4" />
            초기화
          </Button>
        </div>
      </div>

      {isLoading && <LoadingBlock />}
      {error && <ErrorBlock />}

      {data && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>이름</TableHead>
                <TableHead>연락처</TableHead>
                <TableHead>지역</TableHead>
                <TableHead>가맹 유형</TableHead>
                <TableHead>구분</TableHead>
                <TableHead>접수</TableHead>
                <TableHead>최근 접수일</TableHead>
                <TableHead>최근 회차</TableHead>
                <TableHead>참석 요약</TableHead>
                <TableHead>리드</TableHead>
                <TableHead className="text-right">작업</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.content.length === 0 && (
                <EmptyRow colSpan={11}>
                  <Inbox className="mx-auto mb-2 size-8 opacity-40" />
                  접수 고객이 없습니다
                </EmptyRow>
              )}
              {data.content.map((item: IntakeCustomerItem) => (
                <TableRow
                  key={item.contactId}
                  className="cursor-pointer"
                  onClick={() => setDetailContactId(item.contactId)}
                >
                  <TableCell>
                    <span className="flex items-center gap-1.5">
                      {/* 이름도 진입점 — 행 클릭과 같은 동작이지만 키보드로 접근할 수 있다. */}
                      <button
                        type="button"
                        className="font-medium text-primary underline-offset-4 hover:underline"
                        onClick={(e) => {
                          e.stopPropagation()
                          setDetailContactId(item.contactId)
                        }}
                      >
                        {item.name}
                      </button>
                      {item.isTest && <Badge variant="outline">테스트</Badge>}
                    </span>
                  </TableCell>
                  <TableCell className="tabular-nums">{formatPhone(item.phoneNormalized)}</TableCell>
                  <TableCell>{[item.sido, item.sigungu].filter(Boolean).join(' ') || '-'}</TableCell>
                  <TableCell>{applicationLabels(item.franchiseTypes, FRANCHISE_TYPE_LABELS)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{labelOf(INTAKE_CUSTOMER_KIND_LABEL, item.kind)}</Badge>
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">{intakeCountsText(item)}</TableCell>
                  <TableCell className="tabular-nums">{formatKstDate(item.latestReceivedAt)}</TableCell>
                  <TableCell className="tabular-nums">
                    {item.latestSessionDate
                      ? `${item.latestSessionDate}${item.latestSessionMode ? ` · ${labelOf(SESSION_MODE_LABEL, item.latestSessionMode)}` : ''}`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {item.briefingCount > 0 ? (
                      <Badge variant={attendanceSummaryVariant(item.attendanceSummary)}>
                        {labelOf(ATTENDANCE_SUMMARY_LABEL, item.attendanceSummary)}
                      </Badge>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {item.hasActiveLead ? (
                      <Badge variant="secondary">{labelOf(LEAD_STAGE_LABEL, item.leadStage)}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        setDetailContactId(item.contactId)
                      }}
                    >
                      <Users className="size-4" />
                      접수 이력
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Pagination
            page={filter.page ?? 0}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            onChange={(page) => setFilter((prev) => ({ ...prev, page }))}
          />
        </>
      )}

      <ManualIntakeDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        onSuccess={invalidateInbox}
      />

      <Dialog open={exportOriginalOpen} onOpenChange={setExportOriginalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>원본 포함 CSV 내려받기</DialogTitle>
            <DialogDescription>
              접수 건 단위로 마스킹하지 않은 원본 연락처가 포함됩니다. 내려받기 기록이 감사 로그에 남습니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOriginalOpen(false)}>취소</Button>
            <Button
              variant="destructive"
              disabled={exportOriginalMutation.isPending}
              onClick={() => {
                exportOriginalMutation.mutate()
                setExportOriginalOpen(false)
              }}
            >
              {exportOriginalMutation.isPending && <Loader2 className="size-4 animate-spin" />}
              원본 포함 내려받기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {detailContactId != null && (
        <CustomerDetailDialog
          contactId={detailContactId}
          onChanged={invalidateInbox}
          onClose={() => setDetailContactId(null)}
        />
      )}
    </div>
  )
}

function intakeCountsText(item: IntakeCustomerItem): string {
  const parts: string[] = []
  if (item.briefingCount > 0) parts.push(`설명회 ${item.briefingCount}`)
  if (item.franchiseCount > 0) parts.push(`문의 ${item.franchiseCount}`)
  if (item.otherCount > 0) parts.push(`기타 ${item.otherCount}`)
  return parts.join(' · ') || '-'
}

function SummaryTile({ label, value, emphasis }: { label: string; value: number; emphasis?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={emphasis ? 'mt-1 text-2xl font-semibold tabular-nums' : 'mt-1 text-lg font-semibold tabular-nums'}>
        {value.toLocaleString('ko-KR')}
      </p>
    </div>
  )
}

/**
 * 접수 고객 상세 — 그 사람의 접수 전부. 접수(건)의 작업(참석 기록·회차 수정·리드 전환)은 여기서
 * 이력 행을 골라 실행한다.
 */
function CustomerDetailDialog({
  contactId,
  onChanged,
  onClose,
}: {
  contactId: number
  onChanged: () => void
  onClose: () => void
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['lc-crm-inbox-customer-detail', contactId],
    queryFn: () => fetchIntakeCustomerDetail(contactId),
  })
  const [intakeDetailId, setIntakeDetailId] = useState<number | null>(null)
  const [conversionPreview, setConversionPreview] = useState<ConversionPreview | null>(null)
  const [expandedIntakes, setExpandedIntakes] = useState<Record<number, boolean>>({})

  const previewMutation = useMutation({
    mutationFn: (intakeId: number) => previewConversion(intakeId),
    onSuccess: (preview) => setConversionPreview(preview),
    onError: (err) => toast.error(handleApiError(err) || '전환 미리보기 실패'),
  })

  const applyMutation = useMutation({
    mutationFn: (token: string) => applyConversion(token),
    onSuccess: (result) => {
      toast.success(result.isNewLead ? '새 리드가 생성되었습니다' : '기존 리드에 연결되었습니다')
      setConversionPreview(null)
      onChanged()
    },
    onError: (err) => toast.error(handleApiError(err) || '전환 적용 실패'),
  })

  const customer = data?.customer

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-h-[85vh] min-w-0 grid-cols-1 overflow-y-auto [overflow-wrap:anywhere] sm:max-w-3xl">
        <DialogHeader className="min-w-0 pr-6">
          <DialogTitle>{customer ? `${customer.name} · 접수 이력` : '접수 이력'}</DialogTitle>
          <DialogDescription>
            연락처 하나에 묶인 접수 전부입니다. 참석 기록과 리드 전환은 접수 건에서 실행합니다.
          </DialogDescription>
        </DialogHeader>

        {isLoading && <LoadingBlock />}
        {error != null && <ErrorBlock />}

        {data && customer && (
          <div className="flex min-w-0 flex-col gap-4 text-sm">
            <div className="grid min-w-0 grid-cols-1 gap-2 rounded-md border border-border bg-muted/30 p-3 sm:grid-cols-2">
              <DetailRow label="연락처">{formatPhone(customer.phoneNormalized)}</DetailRow>
              <DetailRow label="구분">
                <Badge variant="outline">{labelOf(INTAKE_CUSTOMER_KIND_LABEL, customer.kind)}</Badge>
              </DetailRow>
              <DetailRow label="접수">{intakeCountsText(customer)}</DetailRow>
              <DetailRow label="참석 요약">
                {customer.briefingCount > 0 ? (
                  <Badge variant={attendanceSummaryVariant(customer.attendanceSummary)}>
                    {labelOf(ATTENDANCE_SUMMARY_LABEL, customer.attendanceSummary)}
                  </Badge>
                ) : '-'}
              </DetailRow>
              <DetailRow label="리드">
                {customer.hasActiveLead
                  ? `${labelOf(LEAD_STAGE_LABEL, customer.leadStage)} (#${customer.leadId})`
                  : '없음'}
              </DetailRow>
              <DetailRow label="지역">{[customer.sido, customer.sigungu].filter(Boolean).join(' ') || '-'}</DetailRow>
              <DetailRow label="가맹 유형">{applicationLabels(customer.franchiseTypes, FRANCHISE_TYPE_LABELS)}</DetailRow>
            </div>

            <IntakeHistory
              intakes={data.intakes}
              expandedIntakes={expandedIntakes}
              onToggle={(intakeId, expanded) => setExpandedIntakes((previous) => ({ ...previous, [intakeId]: !expanded }))}
              onDetail={setIntakeDetailId}
              onConvert={(intakeId) => previewMutation.mutate(intakeId)}
              isConverting={previewMutation.isPending}
              onChanged={onChanged}
            />
            <div className="min-w-0 [&_p]:[overflow-wrap:anywhere]">
              <ContactNotesPanel key={contactId} contactId={contactId} />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>닫기</Button>
        </DialogFooter>
      </DialogContent>

      {conversionPreview && (
        <ConversionDialog
          preview={conversionPreview}
          item={data?.intakes.find((i) => i.intakeId === conversionPreview.intakeId)}
          isPending={applyMutation.isPending}
          onApply={() => applyMutation.mutate(conversionPreview.previewToken)}
          onClose={() => setConversionPreview(null)}
        />
      )}

      {intakeDetailId != null && (
        <IntakeDetailDialog
          intakeId={intakeDetailId}
          onChanged={onChanged}
          onClose={() => setIntakeDetailId(null)}
        />
      )}
    </Dialog>
  )
}


const COMPACT_HISTORY_QUERY = '(max-width: 1023px)'

function subscribeToHistoryLayout(onChange: () => void) {
  const media = window.matchMedia(COMPACT_HISTORY_QUERY)
  media.addEventListener('change', onChange)
  return () => media.removeEventListener('change', onChange)
}

function isCompactHistory() {
  return window.matchMedia(COMPACT_HISTORY_QUERY).matches
}

/** Narrow screens use labeled entries so the history never needs a horizontal scroll area. */
function IntakeHistory({
  intakes,
  expandedIntakes,
  onToggle,
  onDetail,
  onConvert,
  isConverting,
  onChanged,
}: {
  intakes: InboxDetail[]
  expandedIntakes: Record<number, boolean>
  onToggle: (intakeId: number, expanded: boolean) => void
  onDetail: (intakeId: number) => void
  onConvert: (intakeId: number) => void
  isConverting: boolean
  onChanged: () => void
}) {
  const compact = useSyncExternalStore(subscribeToHistoryLayout, isCompactHistory, () => false)
  const fields = (intake: InboxDetail): Array<{ label: string; value: ReactNode }> => [
    { label: '접수일', value: formatKstDate(intake.receivedAt) },
    { label: '유형', value: <Badge variant="outline" className="h-auto max-w-full whitespace-normal [overflow-wrap:anywhere]">{labelOf(SOURCE_TYPE_LABEL, intake.sourceType)}</Badge> },
    { label: '유입경로', value: acquisitionChannelLabel(intake.acquisitionChannel) },
    { label: '회차', value: intake.sessionDate
      ? `${intake.sessionDate}${intake.sessionMode ? ` · ${labelOf(SESSION_MODE_LABEL, intake.sessionMode)}` : ''}`
      : '-' },
    { label: '신청 상태', value: labelOf(APPLICATION_STATUS_LABEL, intake.applicationStatus) },
    { label: '참석', value: intake.sourceType === 'BRIEFING'
      ? labelOf(INTAKE_ATTENDANCE_LABEL, intake.attendanceStatus) : '-' },
    { label: '접수 상태', value: <Badge variant={statusBadgeVariant(intake.inboxStatus)} className="h-auto max-w-full whitespace-normal [overflow-wrap:anywhere]">
      {labelOf(INBOX_STATUS_LABEL, intake.inboxStatus)}
    </Badge> },
  ]
  const actions = (intake: InboxDetail, expanded: boolean) => (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      <Button variant="outline" size="sm" aria-expanded={expanded}
        aria-controls={`intake-application-${intake.intakeId}`}
        onClick={() => onToggle(intake.intakeId, expanded)}>
        {expanded ? '신청 정보 접기' : '신청 정보 펼치기'}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => onDetail(intake.intakeId)}>상세</Button>
      {intake.inboxStatus === 'PENDING' && (
        <Button variant="ghost" size="sm" disabled={isConverting} onClick={() => onConvert(intake.intakeId)}>
          {isConverting ? <Loader2 className="size-4 animate-spin" /> : <ArrowRightCircle className="size-4" />}
          리드 전환
        </Button>
      )}
    </div>
  )
  const details = (intake: InboxDetail) => (
    <div id={`intake-application-${intake.intakeId}`} className="flex min-w-0 flex-col gap-4">
      <IntakeApplicationDetails intake={intake} />
      {intake.sourceType === 'MANUAL' && <ManualIntakeEditForm intake={intake} onChanged={onChanged} />}
    </div>
  )

  return (
    <section className="min-w-0" data-testid="crm-intake-history" aria-label="접수 이력 목록">
      {compact ? (
        <div className="flex min-w-0 flex-col gap-3">
          {intakes.map((intake, index) => {
            const expanded = expandedIntakes[intake.intakeId] ?? index === 0
            return (
              <Card key={intake.intakeId} size="sm" data-intake-id={intake.intakeId} className="min-w-0">
                <CardHeader><CardTitle>접수 {index + 1}</CardTitle></CardHeader>
                <CardContent className="flex min-w-0 flex-col gap-4">
                  <dl className="grid min-w-0 grid-cols-1 gap-x-4 gap-y-2 min-[480px]:grid-cols-2">
                    {fields(intake).map(({ label, value }) => (
                      <div key={label} className="flex min-w-0 flex-col gap-1">
                        <dt className="text-muted-foreground">{label}</dt>
                        <dd className="min-w-0 [overflow-wrap:anywhere]">{value}</dd>
                      </div>
                    ))}
                  </dl>
                  {expanded && details(intake)}
                </CardContent>
                <CardFooter className="min-w-0 flex-wrap">{actions(intake, expanded)}</CardFooter>
              </Card>
            )
          })}
        </div>
      ) : (
        <Table className="table-fixed">
          <colgroup>
            {[12, 14, 10, 16, 9, 8, 9, 22].map((width, index) => <col key={index} style={{ width: `${width}%` }} />)}
          </colgroup>
          <TableHeader>
            <TableRow>
              {['접수일', '유형', '유입경로', '회차', '신청 상태', '참석', '접수 상태', '작업'].map((label) => (
                <TableHead key={label} className="whitespace-normal">{label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {intakes.map((intake, index) => {
              const expanded = expandedIntakes[intake.intakeId] ?? index === 0
              return (
                <Fragment key={intake.intakeId}>
                  <TableRow data-intake-id={intake.intakeId}>
                    {fields(intake).map(({ label, value }) => (
                      <TableCell key={label} className="whitespace-normal [overflow-wrap:anywhere]">{value}</TableCell>
                    ))}
                    <TableCell className="whitespace-normal">{actions(intake, expanded)}</TableCell>
                  </TableRow>
                  {expanded && (
                    <TableRow>
                      <TableCell colSpan={8} className="whitespace-normal py-3">{details(intake)}</TableCell>
                    </TableRow>
                  )}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      )}
    </section>
  )
}

function IntakeDetailDialog({
  intakeId,
  onChanged,
  onClose,
}: {
  intakeId: number
  onChanged: () => void
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['lc-crm-intake-detail', intakeId],
    queryFn: () => fetchIntakeDetail(intakeId),
  })
  const [attendance, setAttendance] = useState<string | null>(null)

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['lc-crm-intake-detail', intakeId] })
    onChanged()
  }

  // 2026-09-07 QA: 참석 여부를 접수함에서 직접 고친다. 원장 신청이 있으면 원장에 기록되고 사본이 따라간다.
  const attendanceMutation = useMutation({
    mutationFn: (status: string) => updateIntakeAttendance(intakeId, status),
    onSuccess: (result) => {
      toast.success(
        result.ledgerSynced
          ? '참석 여부를 기록했습니다. 문의관리 설명회 신청에도 반영됩니다.'
          : '참석 여부를 기록했습니다. 원장 신청이 없어 CRM 접수에만 반영됩니다.',
      )
      setAttendance(null)
      refresh()
    },
    onError: (err) => toast.error(handleApiError(err) || '참석 여부 기록 실패'),
  })
  const cancelled = data?.applicationStatus === 'CANCELLED'
  const attendanceValue = attendance ?? data?.attendanceStatus ?? 'UNKNOWN'
  const attendanceDirty = data != null && attendanceValue !== data.attendanceStatus

  // 회차 날짜·방식은 원장이 없는 접수만 여기서 고친다. 원장이 있으면 같은 날짜에 게시 회차가 하나뿐이라
  // 방식 변경이 곧 회차 이동이고, 정원·마감 검사는 문의관리의 회차 이동이 맡는다.
  const ledgerOwned = data?.sourceRefType === 'lc_briefing_application'
  const [sessionDate, setSessionDate] = useState<string | null>(null)
  const [sessionMode, setSessionMode] = useState<string | null>(null)
  const sessionDateValue = sessionDate ?? data?.sessionDate ?? ''
  const sessionModeValue = sessionMode ?? data?.sessionMode ?? ''
  const sessionDirty =
    data != null && (sessionDateValue !== (data.sessionDate ?? '') || sessionModeValue !== (data.sessionMode ?? ''))
  const sessionMutation = useMutation({
    mutationFn: () =>
      updateIntakeSession(intakeId, {
        sessionDate: sessionDateValue !== (data?.sessionDate ?? '') ? sessionDateValue || undefined : undefined,
        sessionMode: sessionModeValue !== (data?.sessionMode ?? '') ? sessionModeValue || undefined : undefined,
      }),
    onSuccess: () => {
      toast.success('회차를 수정했습니다')
      setSessionDate(null)
      setSessionMode(null)
      refresh()
    },
    onError: (err) => toast.error(handleApiError(err) || '회차 수정 실패'),
  })

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-h-[85vh] min-w-0 grid-cols-1 overflow-y-auto [overflow-wrap:anywhere] sm:max-w-lg">
        <DialogHeader className="min-w-0 pr-6">
          <DialogTitle>접수 상세 #{intakeId}</DialogTitle>
          <DialogDescription>원본 신청 정보와 현재 접수 상태입니다.</DialogDescription>
        </DialogHeader>

        {isLoading && <LoadingBlock />}
        {error != null && <ErrorBlock />}

        {data && (
          <div className="flex min-w-0 flex-col gap-4 text-sm">
            <div className="flex min-w-0 flex-col gap-2">
              <DetailRow label="이름">{data.contactName ?? '-'}</DetailRow>
              <DetailRow label="연락처">{formatPhone(data.phoneNormalized)}</DetailRow>
              <DetailRow label="유형">
                <Badge variant="outline">{labelOf(SOURCE_TYPE_LABEL, data.sourceType)}</Badge>
              </DetailRow>
              <DetailRow label="접수 상태">
                <Badge variant={statusBadgeVariant(data.inboxStatus)}>
                  {labelOf(INBOX_STATUS_LABEL, data.inboxStatus)}
                </Badge>
              </DetailRow>
              <DetailRow label="신청 상태">{labelOf(APPLICATION_STATUS_LABEL, data.applicationStatus)}</DetailRow>
              {data.sourceType === 'BRIEFING' && (
                <>
                  {ledgerOwned ? (
                    <>
                      <DetailRow label="회차">
                        {data.sessionDate
                          ? `${data.sessionDate} · ${data.sessionMode ? labelOf(SESSION_MODE_LABEL, data.sessionMode) : '-'}`
                          : '-'}
                      </DetailRow>
                      <p className="text-right text-xs text-muted-foreground">
                        원장 신청이 있는 접수입니다. 회차 날짜·방식(온·오프라인) 변경은 문의관리 &gt; 설명회 신청의
                        회차 이동으로 하면 이 접수가 따라갑니다.
                      </p>
                    </>
                  ) : (
                    <DetailRow label="회차">
                      <span className="flex flex-wrap items-center justify-end gap-2">
                        <Input
                          type="date"
                          aria-label="회차 날짜"
                          className="h-8 w-40"
                          value={sessionDateValue}
                          onChange={(e) => setSessionDate(e.target.value)}
                        />
                        <LabeledSelect
                          size="sm"
                          className="w-28"
                          ariaLabel="회차 방식"
                          value={sessionModeValue || 'unset'}
                          onChange={(next) => setSessionMode(next === 'unset' ? '' : next)}
                          items={[
                            { value: 'unset', label: '미정' },
                            ...Object.entries(SESSION_MODE_LABEL).map(([value, label]) => ({ value, label })),
                          ]}
                        />
                        <Button
                          size="sm"
                          disabled={!sessionDirty || sessionMutation.isPending}
                          onClick={() => sessionMutation.mutate()}
                        >
                          {sessionMutation.isPending && <Loader2 className="size-4 animate-spin" />}
                          저장
                        </Button>
                      </span>
                    </DetailRow>
                  )}
                  <DetailRow label="참석 여부">
                    <span className="flex items-center justify-end gap-2">
                      <LabeledSelect
                        size="sm"
                        className="w-28"
                        ariaLabel="참석 여부"
                        value={attendanceValue}
                        onChange={setAttendance}
                        items={Object.entries(INTAKE_ATTENDANCE_LABEL).map(([value, label]) => ({ value, label }))}
                      />
                      <Button
                        size="sm"
                        disabled={!attendanceDirty || attendanceMutation.isPending || (cancelled && attendanceValue !== 'UNKNOWN')}
                        onClick={() => attendanceMutation.mutate(attendanceValue)}
                      >
                        {attendanceMutation.isPending && <Loader2 className="size-4 animate-spin" />}
                        기록
                      </Button>
                    </span>
                  </DetailRow>
                  <p className="text-right text-xs text-muted-foreground">
                    {cancelled
                      ? '취소된 신청에는 참석을 기록할 수 없습니다.'
                      : data.sourceRefType === 'lc_briefing_application'
                        ? '기록하면 문의관리의 설명회 신청 원장에 쓰이고 이 접수가 따라갑니다.'
                        : '원장 신청이 없는 접수라 CRM 접수에만 기록됩니다.'}
                  </p>
                </>
              )}
              <DetailRow label="지역">
                {[data.sido, data.sigungu].filter(Boolean).join(' ') || '-'}
              </DetailRow>
              <DetailRow label="접수일">{formatKstDate(data.receivedAt)}</DetailRow>
              {data.convertedAt && (
                <DetailRow label="리드 전환">
                  {formatKstDate(data.convertedAt)}
                  {data.convertedBy ? ` · #${data.convertedBy}` : ''}
                </DetailRow>
              )}
              {data.cancelledAt && <DetailRow label="취소일">{formatKstDate(data.cancelledAt)}</DetailRow>}
            </div>

            <div>
              <p className="mb-2 font-semibold">신청 내용</p>
              <IntakeApplicationDetails intake={data} />
            </div>
            {data.sourceType === 'MANUAL' && (
              <ManualIntakeEditForm intake={data} onChanged={refresh} />
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>닫기</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 min-[480px]:flex-row min-[480px]:items-start min-[480px]:justify-between min-[480px]:gap-4">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 max-w-full [overflow-wrap:anywhere] min-[480px]:text-right">{children}</span>
    </div>
  )
}

function ManualIntakeEditForm({ intake, onChanged }: { intake: InboxDetail; onChanged: () => void }) {
  const formId = useId()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [notes, setNotes] = useState('')
  const [channelDraft, setChannelDraft] = useState<{ initial?: AcquisitionChannel; value?: AcquisitionChannel }>({})
  const queryClient = useQueryClient()
  const save = useMutation({
    mutationFn: () => updateManualIntake(intake.intakeId, {
      name: name.trim(), notes: notes.trim() || null,
      ...acquisitionChannelPatch(channelDraft.initial, channelDraft.value),
    }),
    onSuccess: () => {
      setEditing(false)
      queryClient.invalidateQueries({ queryKey: ['lc-crm-intake-detail', intake.intakeId] })
      queryClient.invalidateQueries({ queryKey: ['lc-crm-lead'] })
      queryClient.invalidateQueries({ queryKey: ['lc-crm-leads'] })
      onChanged()
      toast.success('수동 접수 정보를 수정했습니다')
    },
    onError: (error) => toast.error(handleApiError(error) || '수동 접수 수정 실패'),
  })
  if (!editing) return (
    <div>
      <Button variant="outline" size="sm" onClick={() => {
        setName(typeof intake.sourcePayload.name === 'string' ? intake.sourcePayload.name : intake.contactName ?? '')
        setNotes(typeof intake.sourcePayload.notes === 'string' ? intake.sourcePayload.notes : '')
        setChannelDraft({ initial: intake.acquisitionChannel ?? undefined, value: intake.acquisitionChannel ?? undefined })
        setEditing(true)
      }}>접수 정보 수정</Button>
    </div>
  )
  return (
    <form className="flex min-w-0 flex-col gap-3" onSubmit={(event) => {
      event.preventDefault()
      if (name.trim() && !save.isPending) save.mutate()
    }}>
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor={`${formId}-name`}>이름</FieldLabel>
          <Input id={`${formId}-name`} value={name} maxLength={50}
            disabled={save.isPending} onChange={(event) => setName(event.target.value)} />
        </Field>
        <AcquisitionChannelField id={`${formId}-acquisition-channel`} value={channelDraft.value}
          disabled={save.isPending} onChange={(value) => setChannelDraft((current) => ({ ...current, value }))} />
        <Field>
          <FieldLabel htmlFor={`${formId}-notes`}>비고</FieldLabel>
          <Textarea id={`${formId}-notes`} rows={3} value={notes} maxLength={10000}
            disabled={save.isPending} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </FieldGroup>
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" disabled={save.isPending} onClick={() => setEditing(false)}>취소</Button>
        <Button type="submit" disabled={!name.trim() || save.isPending}>수정 저장</Button>
      </div>
    </form>
  )
}

function ManualIntakeDialog({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  onSuccess: () => void
}) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [notes, setNotes] = useState('')
  const [acquisitionChannel, setAcquisitionChannel] = useState<AcquisitionChannel>()

  const mutation = useMutation({
    mutationFn: () => createManualIntake({ name, phone, sourceType: 'MANUAL', notes: notes || undefined, acquisitionChannel }),
    onSuccess: () => {
      toast.success('수동 접수가 등록되었습니다')
      onOpenChange(false)
      setName('')
      setPhone('')
      setNotes('')
      setAcquisitionChannel(undefined)
      onSuccess()
    },
    onError: (err) => toast.error(handleApiError(err) || '수동 등록 실패'),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>수동 접수 등록</DialogTitle>
          <DialogDescription>전화·방문 등 시스템 외부에서 접수된 건을 등록합니다</DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="manual-name">이름</FieldLabel>
            <Input
              id="manual-name"
              placeholder="이름"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="manual-phone">전화번호</FieldLabel>
            <Input
              id="manual-phone"
              placeholder="01012345678"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Field>
          <AcquisitionChannelField id="manual-acquisition-channel" value={acquisitionChannel}
            disabled={mutation.isPending} onChange={setAcquisitionChannel} />
          <Field>
            <FieldLabel htmlFor="manual-notes">비고</FieldLabel>
            <Textarea
              id="manual-notes"
              rows={3}
              placeholder="메모 (선택)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>
        </FieldGroup>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>취소</Button>
          <Button
            disabled={!name.trim() || !phone.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            등록
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ConversionDialog({
  preview,
  isPending,
  item,
  onApply,
  onClose,
}: {
  preview: ConversionPreview
  item?: InboxListItem
  isPending: boolean
  onApply: () => void
  onClose: () => void
}) {
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>리드 전환</DialogTitle>
          <DialogDescription>
            접수를 리드로 전환합니다. 전환 후에는 파이프라인에서 관리됩니다.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">접수 ID</span>
            <span className="tabular-nums">{preview.intakeId}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">연락처</span>
            <span>{preview.contactName}</span>
          </div>
          {item && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">신청</span>
              <span>
                {labelOf(SOURCE_TYPE_LABEL, item.sourceType)}
                {item.sessionDate ? ` · ${item.sessionDate}` : ''}
                {item.sourceType === 'BRIEFING'
                  ? ` · ${labelOf(INTAKE_ATTENDANCE_LABEL, item.attendanceStatus)}`
                  : ''}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">전환 유형</span>
            <Badge variant={preview.willCreateNewLead ? 'default' : 'secondary'}>
              {preview.willCreateNewLead ? '새 리드 생성' : '기존 리드에 연결'}
            </Badge>
          </div>
          {preview.existingLeadId && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">기존 리드 ID</span>
              <span className="tabular-nums">{preview.existingLeadId}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button disabled={isPending} onClick={onApply}>
            {isPending && <Loader2 className="size-4 animate-spin" />}
            전환 적용
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
